import React, { useEffect, useState, useRef } from 'react';
import apiClient from '../utils/apiClient';
import { getCurrentUser, User } from '../services/userService';
import { Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Tabs,
    Tab,
    Typography,
    Paper
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { EMPTY_TRANSACTION, TEST_TRANSACTION } from '../constants';
import { Transaction } from '../types';
import {
    DataProviderInputMappingColumnsModel,
    getDataProviderInputMappingColumns
} from "../services/dataProviderInputMappingService.ts";
import {lowerFirstLetter} from "../utils/nameUtils.ts";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`transaction-tabpanel-${index}`}
            aria-labelledby={`transaction-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `transaction-tab-${index}`,
        'aria-controls': `transaction-tabpanel-${index}`,
    };
}

const TransactionAdd = () => {
    const navigate = useNavigate();
    const formRef = useRef<HTMLDivElement>(null);
    const [pbms, setPbms] = useState<Array<{ id: number; pbmName: string }>>([]);
    const [transaction, setTransaction] = useState<Transaction>(EMPTY_TRANSACTION);
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
    const [tabValue, setTabValue] = useState(0);
    const [selectedPbmId, setSelectedPbmId] = useState<number | null>(null);
    const [uploadStatus, setUploadStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [uploadResult, setUploadResult] = useState<any>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [dataProviderMappings, setDataProviderMappings] = useState<DataProviderInputMappingColumnsModel[]>([]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        console.log("[handleTabChange] Logging event so that we can get the build to work. Lame", event)
        setTabValue(newValue);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [pbmsData, userData] = await Promise.all([
                    apiClient.get<Array<{ id: number; pbmName: string }>>('/api/PBM'),
                    getCurrentUser()
                ]);
                
                setPbms(pbmsData);
                setCurrentUser(userData);
                
                // Initialize transaction with user's organization if they are a Broker
                if (userData && userData.role?.name === 'Broker') {
                    setTransaction(prev => ({
                        ...prev,
                        organizationId: userData.organizationId
                    }));
                }
            } catch (err) {
                console.error("[TransactionAdd] Error fetching initial data", err);
            } 
        };
        
        fetchData();
    }, []);

    useEffect(() => {
        const fetchDataProviderMappings = async () => {
            try{
                const mappingsData = await getDataProviderInputMappingColumns('Nash Default');
                setDataProviderMappings(mappingsData);
            } catch (error) {
                console.log('Error fetching data provider mappings', error);
            }
        }

        fetchDataProviderMappings();
    }, []);

    const onDrop = React.useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            setUploadFile(acceptedFiles[0]);
            setUploadError(null);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 1,
        multiple: false
    });

    const handleFileUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!uploadFile) {
            setUploadError("Please select a file to upload");
            return;
        }

        if (!selectedPbmId) {
            setUploadError("Please select a PBM");
            return;
        }

        const selectedPbm = pbms.find(pbm => pbm.id === selectedPbmId);
        if (!selectedPbm) {
            setUploadError("Invalid PBM selected");
            return;
        }

        setUploadStatus('loading');
        setUploadError(null);
        
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('pbmName', selectedPbm.pbmName);

        try {
            // Use apiClient to handle authentication
            const data = await apiClient.post('/api/FileUpload', formData, {
                headers: {
                    // Remove Content-Type header to let the browser set it with the boundary parameter
                    'Content-Type': undefined
                }
            });
            
            setUploadStatus('success');
            setUploadResult(data);
        } catch (error) {
            setUploadStatus('error');
            console.error('File upload error:', error);
            setUploadError(error instanceof Error ? error.message : 'An unknown error occurred');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFieldErrors({});
        
        // Ensure transaction has organizationId if user is a Broker
        const transactionToSubmit = { 
            ...transaction,
            // Set organizationId from current user if they are a Broker
            organizationId: currentUser?.role?.name === 'Broker' 
                ? currentUser.organizationId 
                : transaction.organizationId
        };
        
        try {
            await apiClient.post('/api/transaction', transactionToSubmit);
            navigate('/transactions');
        } catch (error) {
            if (error instanceof Error && error.message) {
                try {
                    const errorData = JSON.parse(error.message);
                    if (errorData.Field && errorData.Message) {
                        setFieldErrors({ [errorData.Field]: errorData.Message });
                    }
                } catch {
                    console.error('[Error Submitting Transaction]', error);
                }
            }
            formRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const fillWithTestData = () => {
        setTransaction({
            ...TEST_TRANSACTION
        });
    };

    const handleDateChange = (field: keyof Transaction, value: any) => {
        setTransaction(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const getLabel = (field: keyof Transaction, defaultLabel: string) => {
        const mapping = dataProviderMappings.find(x => lowerFirstLetter(x.destinationColumn) == field);
        return mapping?.inputColumn ?? defaultLabel;
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box ref={formRef} sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
                <Card>
                    <CardHeader title="Add New Transaction" />
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="transaction tabs">
                            <Tab label="File Upload" {...a11yProps(0)} />
                            <Tab label="Manual Entry" {...a11yProps(1)} />
                        </Tabs>
                    </Box>
                    <CardContent>
                        <TabPanel value={tabValue} index={0}>
                            <form onSubmit={handleFileUpload}>
                                <Stack spacing={3}>
                                    <Typography variant="h6">Upload Transaction File</Typography>
                                    
                                    {uploadError && (
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            {uploadError}
                                        </Alert>
                                    )}
                                    
                                    {uploadStatus === 'success' && (
                                        <Alert severity="success" sx={{ mb: 2 }}>
                                            File uploaded successfully!
                                        </Alert>
                                    )}
                                    
                                    <FormControl fullWidth>
                                        <InputLabel>PBM</InputLabel>
                                        <Select
                                            value={selectedPbmId || ""}
                                            label="PBM"
                                            onChange={(e) => setSelectedPbmId(Number(e.target.value))}
                                            required
                                        >
                                            {pbms && Array.isArray(pbms) ? pbms.map(pbm => (
                                                <MenuItem key={pbm.id} value={pbm.id}>{pbm.pbmName}</MenuItem>
                                            )) : <></>}
                                        </Select>
                                    </FormControl>
                                    
                                    <Paper
                                        {...getRootProps()}
                                        sx={{
                                            p: 3,
                                            border: '2px dashed',
                                            borderColor: isDragActive ? 'primary.main' : 'grey.300',
                                            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                backgroundColor: 'action.hover'
                                            },
                                            mb: 2
                                        }}
                                    >
                                        <input {...getInputProps()} />
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 2
                                            }}
                                        >
                                            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                                            <Typography variant="h6" component="div">
                                                {isDragActive
                                                    ? 'Drop the file here'
                                                    : 'Drag and drop a file here, or click to select a file'}
                                            </Typography>
                                        </Box>
                                    </Paper>

                                    {uploadFile && (
                                        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Selected File:
                                            </Typography>
                                            <Typography variant="body2">
                                                {uploadFile.name} ({(uploadFile.size / 1024).toFixed(2)} KB)
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        disabled={uploadStatus === 'loading' || !uploadFile || !selectedPbmId}
                                    >
                                        {uploadStatus === 'loading' ? 'Uploading...' : 'Upload File'}
                                    </Button>
                                    
                                    {uploadStatus === 'success' && uploadResult && (
                                        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Upload Details:
                                            </Typography>
                                            <Typography variant="body2">
                                                File: {uploadResult.originalFileName}
                                            </Typography>
                                            <Typography variant="body2">
                                                Uploaded to: {uploadResult.containerName}/{uploadResult.directory}/{uploadResult.fileName}
                                            </Typography>
                                            <Typography variant="body2">
                                                Timestamp: {new Date(uploadResult.uploadedAt).toLocaleString()}
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </form>
                        </TabPanel>
                        <TabPanel value={tabValue} index={1}>
                        <form onSubmit={handleSubmit}>
                            {fieldErrors.general && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {fieldErrors.general}
                                </Alert>
                            )}
                            <Stack spacing={2}>
                                <FormControl fullWidth>
                                    <InputLabel>PBM</InputLabel>
                                    <Select
                                        value={transaction.pbmId  || ""}
                                        label="PBM"
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, pbmId: Number(e.target.value) });
                                            if (fieldErrors.pbmId) {
                                                setFieldErrors(prev => {
                                                    const { pbmId, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        required
                                        error={!!fieldErrors.pbmId}
                                    >
                                        {pbms && Array.isArray(pbms) ? pbms.map(pbm => (
                                            <MenuItem key={pbm.id} value={pbm.id}>{pbm.pbmName}</MenuItem>
                                        )) : <></>}
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    label={getLabel('pharmNabp', 'Pharm NABP')}
                                    value={transaction.pharmNabp || ""}
                                    onChange={(e) => {
                                        setTransaction({ ...transaction, pharmNabp: e.target.value });
                                        if (fieldErrors.pharmNabp) {
                                            setFieldErrors(prev => {
                                                const { pharmNabp, ...rest } = prev;
                                                return rest;
                                            });
                                        }
                                    }}
                                    error={!!fieldErrors.pharmNabp}
                                    helperText={fieldErrors.pharmNabp}
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('rxNum', 'Rx Number')}
                                    value={transaction.rxNum || ""}
                                    onChange={(e) => setTransaction({ ...transaction, rxNum: e.target.value })}
                                    error={!!fieldErrors.rxNum}
                                    helperText={fieldErrors.rxNum}
                                />
                                <FormControl fullWidth>
                                    <DatePicker
                                        label={getLabel('dateFilled', 'Date Filled')}
                                        value={transaction.dateFilled}
                                        onChange={(value) => handleDateChange('dateFilled', value)}
                                    />
                                </FormControl>
                                    <TextField
                                        fullWidth
                                        label={getLabel('groupId', 'Group ID')}
                                        value={transaction.groupId || ""}
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, groupId: e.target.value });
                                            if (fieldErrors.groupId) {
                                                setFieldErrors(prev => {
                                                    const { groupId, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        required
                                        error={!!fieldErrors.groupId}
                                        helperText={fieldErrors.groupId}
                                    />

                                    <TextField
                                        fullWidth
                                        label={getLabel('cardHolderId', 'Card Holder ID')}
                                        value={transaction.cardHolderId || ""}
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, cardHolderId: e.target.value });
                                            if (fieldErrors.cardHolderId) {
                                                setFieldErrors(prev => {
                                                    const { cardHolderId, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        required
                                        inputProps={{ maxLength: 45 }}
                                        error={!!fieldErrors.cardHolderId}
                                        helperText={fieldErrors.cardHolderId}
                                    />

                                    <TextField
                                        fullWidth
                                        label={getLabel('submittedPc', 'Submitted PC')}
                                        value={transaction.submittedPc || ""}
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, submittedPc: e.target.value });
                                            if (fieldErrors.submittedPc) {
                                                setFieldErrors(prev => {
                                                    const { submittedPc, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        inputProps={{ maxLength: 3 }}
                                        error={!!fieldErrors.submittedPc}
                                        helperText={fieldErrors.submittedPc}
                                    />
                                <FormControl fullWidth>
                                    <DatePicker
                                        label={getLabel('dateOfBirth', 'Date of Birth')}
                                        value={transaction.dateOfBirth}
                                        onChange={(value) => handleDateChange('dateOfBirth', value)}
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <InputLabel>{getLabel('genderCode', 'Gender')}</InputLabel>
                                    <Select
                                        value={transaction.genderCode || ""}
                                        label={getLabel('genderCode', 'Gender')}
                                        onChange={(e) => setTransaction({ ...transaction, genderCode: e.target.value })}
                                        required
                                    >
                                        <MenuItem value="Not Specified">Not Specified</MenuItem>
                                        <MenuItem value="Male">Male</MenuItem>
                                        <MenuItem value="Female">Female</MenuItem>
                                        <MenuItem value="Non Binary">Non Binary</MenuItem>
                                    </Select>
                                </FormControl>

                                    <TextField
                                        fullWidth
                                        label={getLabel('patientFirstName', 'Patient First Name')}
                                        value={transaction.patientFirstName || ""}
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, patientFirstName: e.target.value });
                                            if (fieldErrors.patientFirstName) {
                                                setFieldErrors(prev => {
                                                    const { patientFirstName, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        required
                                        inputProps={{ maxLength: 12 }}
                                        error={!!fieldErrors.patientFirstName}
                                        helperText={fieldErrors.patientFirstName}
                                    />

                                    <TextField
                                        fullWidth
                                        label={getLabel('patientLastName', 'Patient Last Name')}
                                        value={transaction.patientLastName || ""}
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, patientLastName: e.target.value });
                                            if (fieldErrors.patientLastName) {
                                                setFieldErrors(prev => {
                                                    const { patientLastName, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        required
                                        inputProps={{ maxLength: 15 }}
                                        error={!!fieldErrors.patientLastName}
                                        helperText={fieldErrors.patientLastName}
                                    />

                                    <TextField
                                        fullWidth
                                        label={getLabel('patientMiddleName', 'Patient Middle Name')}
                                        value={transaction.patientMiddleName || ""}
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, patientMiddleName: e.target.value });
                                            if (fieldErrors.patientMiddleName) {
                                                setFieldErrors(prev => {
                                                    const { patientMiddleName, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        inputProps={{ maxLength: 100 }}
                                        error={!!fieldErrors.patientMiddleName}
                                        helperText={fieldErrors.patientMiddleName}
                                    />

                                    <TextField
                                        fullWidth
                                        label={getLabel('claimId', 'Claim ID')}
                                        value={transaction.claimId || ""}
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, claimId: e.target.value });
                                            if (fieldErrors.claimId) {
                                                setFieldErrors(prev => {
                                                    const { claimId, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        inputProps={{ maxLength: 100 }}
                                        error={!!fieldErrors.claimId}
                                        helperText={fieldErrors.claimId}
                                    />

                                    <TextField
                                        fullWidth
                                        label={getLabel('otherId', 'Other ID')}
                                        value={transaction.otherId || ""}
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, otherId: e.target.value });
                                            if (fieldErrors.otherId) {
                                                setFieldErrors(prev => {
                                                    const { otherId, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        inputProps={{ maxLength: 100 }}
                                        error={!!fieldErrors.otherId}
                                        helperText={fieldErrors.otherId}
                                    />

                                    <TextField
                                        fullWidth
                                        label={getLabel('nashPatientId', 'NASH Patient ID')}
                                        value={transaction.nashPatientId || ""}
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, nashPatientId: e.target.value });
                                            if (fieldErrors.nashPatientId) {
                                                setFieldErrors(prev => {
                                                    const { nashPatientId, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        inputProps={{ maxLength: 100 }}
                                        error={!!fieldErrors.nashPatientId}
                                        helperText={fieldErrors.nashPatientId}
                                    />

                                    <TextField
                                        fullWidth
                                        label={getLabel('nashReferralOrg', 'NASH Referral Organization')}
                                        value={transaction.nashReferralOrg || ""}
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, nashReferralOrg: e.target.value });
                                            if (fieldErrors.nashReferralOrg) {
                                                setFieldErrors(prev => {
                                                    const { nashReferralOrg, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        inputProps={{ maxLength: 100 }}
                                        error={!!fieldErrors.nashReferralOrg}
                                        helperText={fieldErrors.nashReferralOrg}
                                    />

                                    <TextField
                                        fullWidth
                                        label={getLabel('drugDispensed', 'Drug Dispensed')}
                                        value={transaction.drugDispensed || ""}
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, drugDispensed: e.target.value });
                                            if (fieldErrors.drugDispensed) {
                                                setFieldErrors(prev => {
                                                    const { drugDispensed, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        inputProps={{ maxLength: 100 }}
                                        error={!!fieldErrors.drugDispensed}
                                        helperText={fieldErrors.drugDispensed}
                                    />

                                    <TextField
                                        fullWidth
                                        label={getLabel('drugStrength', 'Drug Strength')}
                                        value={transaction.drugStrength || ""}
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, drugStrength: e.target.value });
                                            if (fieldErrors.drugStrength) {
                                                setFieldErrors(prev => {
                                                    const { drugStrength, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        inputProps={{ maxLength: 100 }}
                                        error={!!fieldErrors.drugStrength}
                                        helperText={fieldErrors.drugStrength}
                                    />

                                    <FormControl fullWidth>
                                        <DatePicker
                                            label={getLabel('priorAuthApprovalDate', 'Prior Auth Approval Date')}
                                            value={transaction.priorAuthApprovalDate}
                                            onChange={(value) => handleDateChange('priorAuthApprovalDate', value)}
                                        />
                                    </FormControl>

                                    <FormControl fullWidth>
                                        <DatePicker
                                            label={getLabel('priorAuthExpirationDate', 'Prior Auth Expiration Date')}
                                            value={transaction.priorAuthExpirationDate}
                                            onChange={(value) => handleDateChange('priorAuthExpirationDate', value)}
                                        />
                                    </FormControl>

                                    <TextField
                                        fullWidth
                                        label={getLabel('travelCost', 'Travel Cost')}
                                        type="number"
                                        value={transaction.travelCost || ""}
                                        onChange={(e) => setTransaction({ ...transaction, travelCost: parseFloat(e.target.value) })}
                                        inputProps={{ step: "0.01" }}
                                    />

                                <TextField
                                    fullWidth
                                    label={getLabel('refillCode', 'Refill Code')}
                                    type="number"
                                    value={transaction.refillCode || 0}
                                    onChange={(e) => setTransaction({ ...transaction, refillCode: parseInt(e.target.value) })}
                                    required
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('daysSupply', 'Days Supply')}
                                    type="number"
                                    value={transaction.daysSupply || 0}
                                    onChange={(e) => setTransaction({ ...transaction, daysSupply: parseInt(e.target.value) })}
                                    required
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('ndc', 'NDC')}
                                    value={transaction.ndc || ""}
                                    onChange={(e) => setTransaction({ ...transaction, ndc: e.target.value })}
                                    required
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('ingrCostSubmitted', 'Ingredient Cost Submitted')}
                                    type="number"
                                    value={transaction.ingrCostSubmitted || ""}
                                    onChange={(e) => setTransaction({ ...transaction, ingrCostSubmitted: parseFloat(e.target.value) })}
                                    required
                                    inputProps={{ step: "0.01" }}
                                />

                                    <TextField
                                        fullWidth
                                        label={getLabel('prescriberId', 'Prescriber ID')}
                                        value={transaction.prescriberId || ""}
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, prescriberId: e.target.value });
                                            if (fieldErrors.prescriberId) {
                                                setFieldErrors(prev => {
                                                    const { prescriberId, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        inputProps={{ maxLength: 10 }}
                                        error={!!fieldErrors.prescriberId}
                                        helperText={fieldErrors.prescriberId}
                                    />
                                <FormControl fullWidth>
                                    <DatePicker
                                        label={getLabel('dateWritten', 'Date Written')}
                                        value={transaction.dateWritten}
                                        onChange={(value) => handleDateChange('dateWritten', value)}
                                    />
                                </FormControl>
                                <TextField
                                    fullWidth
                                    label={getLabel('numRefillsAuthorized', 'Number of Refills Authorized')}
                                    type="number"
                                    value={transaction.numRefillsAuthorized || ""}
                                    onChange={(e) => setTransaction({ ...transaction, numRefillsAuthorized: parseInt(e.target.value) })}
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('unc', 'UNC')}
                                    type="number"
                                    value={transaction.unc || ""}
                                    onChange={(e) => setTransaction({ ...transaction, unc: parseFloat(e.target.value) })}
                                    inputProps={{ step: "0.01" }}
                                />

                                <TextField
                                    fullWidth
                                    label={getLabel('qtyDispensed', 'Quantity Dispensed')}
                                    type="number"
                                    value={transaction.qtyDispensed || ""}
                                    onChange={(e) => setTransaction({ ...transaction, qtyDispensed: parseFloat(e.target.value) })}
                                    inputProps={{ step: "0.01" }}
                                />

                                    <TextField
                                        fullWidth
                                        label={getLabel('claimMessage', 'Claim Message')}
                                        value={transaction.claimMessage || ""}
                                        onChange={(e) => {
                                            setTransaction({ ...transaction, claimMessage: e.target.value });
                                            if (fieldErrors.claimMessage) {
                                                setFieldErrors(prev => {
                                                    const { claimMessage, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        inputProps={{ maxLength: 200 }}
                                        error={!!fieldErrors.claimMessage}
                                        helperText={fieldErrors.claimMessage}
                                    />
                                <FormControl fullWidth>
                                    <DatePicker
                                        label={getLabel('dateEnteredByPharmacy', 'Entered Date')}
                                        value={transaction.dateEnteredByPharmacy}
                                        onChange={(value) => handleDateChange('dateEnteredByPharmacy', value)}
                                    />
                                </FormControl>

                                <TextField
                                  fullWidth
                                  label={getLabel('statusCode', 'Status Code')}
                                  value={transaction.statusCode || ""}
                                  onChange={(e) => setTransaction({ ...transaction, statusCode: e.target.value })}
                                  error={!!fieldErrors.statusCode}
                                  helperText={fieldErrors.statusCode}
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('serviceProviderId', 'Service Provider ID')}
                                  type="number"
                                  value={transaction.serviceProviderId || ""}
                                  onChange={(e) => setTransaction({ ...transaction, serviceProviderId: parseInt(e.target.value) })}
                                  inputProps={{ step: "1" }}
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('copay', 'Copay')}
                                  type="number"
                                  value={transaction.copay || ""}
                                  onChange={(e) => setTransaction({ ...transaction, copay: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  required
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('patientPayAmount', 'Patient Pay Amount')}
                                  type="number"
                                  value={transaction.patientPayAmount || ""}
                                  onChange={(e) => setTransaction({ ...transaction, patientPayAmount: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  required
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('pharmacyTotalPaid', 'Pharmacy Total Paid')}
                                  type="number"
                                  value={transaction.pharmacyTotalPaid || ""}
                                  onChange={(e) => setTransaction({ ...transaction, pharmacyTotalPaid: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  required
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('pharmacyIngredientCostPaid', 'Pharmacy Ingredient Cost Paid')}
                                  type="number"
                                  value={transaction.pharmacyIngredientCostPaid || ""}
                                  onChange={(e) => setTransaction({ ...transaction, pharmacyIngredientCostPaid: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  required
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('pharmacyDispensingFeePaid', 'Pharmacy Dispensing Fee Paid')}
                                  type="number"
                                  value={transaction.pharmacyDispensingFeePaid || ""}
                                  onChange={(e) => setTransaction({ ...transaction, pharmacyDispensingFeePaid: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  required
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('pharmacyTaxPaid', 'Pharmacy Tax Paid')}
                                  type="number"
                                  value={transaction.pharmacyTaxPaid || ""}
                                  onChange={(e) => setTransaction({ ...transaction, pharmacyTaxPaid: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  required
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('sellIngredientCost', 'Sell Ingredient Cost')}
                                  type="number"
                                  value={transaction.sellIngredientCost || ""}
                                  onChange={(e) => setTransaction({ ...transaction, sellIngredientCost: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  required
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('sellDispensingFee', 'Sell Dispensing Fee')}
                                  type="number"
                                  value={transaction.sellDispensingFee || ""}
                                  onChange={(e) => setTransaction({ ...transaction, sellDispensingFee: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  required
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('sellTax', 'Sell Tax')}
                                  type="number"
                                  value={transaction.sellTax || ""}
                                  onChange={(e) => setTransaction({ ...transaction, sellTax: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  required
                                />

                                <TextField
                                  fullWidth
                                  label={getLabel('totalClientBilled', 'Total Client Billed')}
                                  type="number"
                                  value={transaction.totalClientBilled || ""}
                                  onChange={(e) => setTransaction({ ...transaction, totalClientBilled: parseFloat(e.target.value) })}
                                  inputProps={{ step: "0.01" }}
                                  required
                                />
                                
                                <TextField
                                    fullWidth
                                    label={getLabel('planPay', 'Plan Pay')}
                                    type="number"
                                    value={transaction.planPay || ""}
                                    onChange={(e) => setTransaction({ ...transaction, planPay: parseFloat(e.target.value) })}
                                    inputProps={{ step: "0.01" }}
                                    required
                                />

                                <FormControl fullWidth>
                                    <DateTimePicker
                                      label={getLabel('processDateTime', 'Process Date / Time')}
                                      value={transaction.processDateTime}
                                      onChange={(value) => handleDateChange('processDateTime', value)}
                                    />
                                </FormControl>

                                
                                <Stack direction="row" spacing={2}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        className="mt-4"
                                        fullWidth
                                    >
                                        Save Transaction
                                    </Button>

                                    <Button
                                        type="button"
                                        variant="outlined"
                                        className="mt-4"
                                        fullWidth
                                        onClick={fillWithTestData}
                                    >
                                        Fill with test data
                                    </Button>
                                </Stack>
                            </Stack>
                        </form>
                        </TabPanel>
                    </CardContent>
                </Card>
            </Box>
        </LocalizationProvider>
    );
}

export default TransactionAdd;
